import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Clock } from "lucide-react";
import { BUSINESS, HEALTH_DISCLAIMER, verifiedSocial, whatsappLink } from "@/config/site";
import { PRODUCTS } from "@/data/products";
import { Wordmark } from "@/components/site/Header";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { trackEvent } from "@/lib/analytics";

const FooterHeading = ({ children }) => <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#cfe2c9]" style={{ fontFamily: "var(--font-body)" }}>{children}</h2>;
const linkCls = "text-sm text-[#e5ede2] hover:text-white hover:underline underline-offset-4";

export const Footer = () => (
  <footer className="surface-green" data-testid="site-footer">
    <div className="container-site py-14 sm:py-16">
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Wordmark light />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#dfe9dc]">
            A South African natural wellness brand that sources, packages, supplies and distributes natural wellness products from Gugulethu, Cape Town.
          </p>
          <p className="mt-4 text-sm text-[#dfe9dc]">Quality · Integrity · Wellness</p>
          {verifiedSocial.length ? (
            <ul className="mt-4 flex gap-3">
              {verifiedSocial.map((s) => (
                <li key={s.name}>
                  <a href={s.url} className={linkCls} target="_blank" rel="noopener noreferrer">
                    {s.name}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="lg:col-span-3">
          <FooterHeading>Products</FooterHeading>
          <ul className="space-y-2.5">
            {PRODUCTS.map((p) => (
              <li key={p.slug}>
                <Link to={p.path} className={linkCls} data-testid={`footer-product-link-${p.slug}`}>
                  {p.name}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/products" className={linkCls}>
                All products
              </Link>
            </li>
          </ul>
        </div>
        <div className="lg:col-span-2">
          <FooterHeading>Company</FooterHeading>
          <ul className="space-y-2.5">
            {[
              ["/about", "About Us"],
              ["/wellness-guide", "Wellness Guide"],
              ["/faqs", "FAQs"],
              ["/contact", "Contact"],
            ].map(([to, l]) => (
              <li key={to}>
                <Link to={to} className={linkCls}>
                  {l}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-3">
          <FooterHeading>Contact</FooterHeading>
          <ul className="space-y-3 text-sm text-[#e5ede2]">
            <li className="flex gap-2.5">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <address className="not-italic leading-relaxed">
                {BUSINESS.address.line1}
                <br />
                {BUSINESS.address.suburb}, {BUSINESS.address.city}, {BUSINESS.address.postalCode}
              </address>
            </li>
            <li className="flex gap-2.5">
              <Phone className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <a href={`tel:${BUSINESS.phoneE164}`} className={linkCls} onClick={() => trackEvent("phone_click", { location: "footer" })} data-testid="footer-phone-link">
                {BUSINESS.phoneDisplay}
              </a>
            </li>
            <li className="flex gap-2.5">
              <WhatsAppIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className={linkCls} onClick={() => trackEvent("whatsapp_click", { location: "footer" })} data-testid="footer-whatsapp-link">
                WhatsApp {BUSINESS.whatsappDisplay}
              </a>
            </li>
            <li className="flex gap-2.5">
              <Mail className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <a href={`mailto:${BUSINESS.email}`} className={`${linkCls} break-all`} onClick={() => trackEvent("email_click", { location: "footer" })} data-testid="footer-email-link">
                {BUSINESS.email}
              </a>
            </li>
            <li className="flex gap-2.5">
              <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                {BUSINESS.hours}
                <span className="block text-xs text-[#cfe2c9]">Operating days to be confirmed</span>
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 rounded-2xl bg-white/[0.07] p-5 text-xs leading-relaxed text-[#dfe9dc] sm:p-6" data-testid="footer-health-disclaimer">
        <p className="mb-1.5 font-semibold text-[#FAF9F5]">Health disclaimer</p>
        <p>{HEALTH_DISCLAIMER.join(" ")}</p>
      </div>

      <div className="mt-8 flex flex-col gap-4 border-t border-white/15 pt-6 text-xs text-[#cfe2c9] md:flex-row md:items-center md:justify-between">
        <p>
          © {new Date().getFullYear()} Uhlumo Organics · Reg. No. {BUSINESS.registrationNumber} · Cape Town, South Africa
        </p>
        <ul className="flex flex-wrap gap-x-5 gap-y-2">
          {[
            ["/privacy-policy", "Privacy Policy"],
            ["/terms", "Terms & Conditions"],
            ["/delivery-returns", "Delivery & Returns"],
            ["/health-disclaimer", "Health Disclaimer"],
          ].map(([to, l]) => (
            <li key={to}>
              <Link to={to} className="hover:text-white hover:underline underline-offset-4" data-testid={`footer-legal-${to.slice(1)}`}>
                {l}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </footer>
);
